import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, Box, Button, Stack, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CropIcon from '@mui/icons-material/Crop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const ACCENT = '#0e7c86';
const ACCENT_DARK = '#0a5a62';
const MIN = 24;

// Handles: which edges each one controls (l/r/t/b) + cursor.
const HANDLES = [
  { k: 'nw', l: 1, t: 1, cur: 'nwse-resize' },
  { k: 'n', t: 1, cur: 'ns-resize' },
  { k: 'ne', r: 1, t: 1, cur: 'nesw-resize' },
  { k: 'e', r: 1, cur: 'ew-resize' },
  { k: 'se', r: 1, b: 1, cur: 'nwse-resize' },
  { k: 's', b: 1, cur: 'ns-resize' },
  { k: 'sw', l: 1, b: 1, cur: 'nesw-resize' },
  { k: 'w', l: 1, cur: 'ew-resize' },
];

// Crop dialog: drag inside to move, drag any edge/corner handle to resize.
export default function ImageCropper({ open, image, onCancel, onCrop }) {
  const imgRef = useRef(null);
  const drag = useRef(null);
  const [rect, setRect] = useState({ x: 20, y: 20, w: 160, h: 90 });
  const [ready, setReady] = useState(false);

  useEffect(() => { if (!open) setReady(false); }, [open]);

  const reset = useCallback(() => {
    const el = imgRef.current; if (!el) return;
    const dw = el.clientWidth, dh = el.clientHeight;
    setRect({ x: dw * 0.08, y: dh * 0.08, w: dw * 0.84, h: dh * 0.84 });
  }, []);
  const onImgLoad = () => { reset(); setReady(true); };

  const down = (mode, e) => {
    e.preventDefault(); e.stopPropagation();
    const p = e.touches ? e.touches[0] : e;
    drag.current = { mode, sx: p.clientX, sy: p.clientY, rect: { ...rect } };
  };
  const onMove = useCallback((e) => {
    if (!drag.current || !imgRef.current) return;
    const p = e.touches ? e.touches[0] : e;
    const W = imgRef.current.clientWidth, H = imgRef.current.clientHeight;
    const dx = p.clientX - drag.current.sx, dy = p.clientY - drag.current.sy;
    const r0 = drag.current.rect;
    if (drag.current.mode === 'move') {
      setRect({ ...r0, x: Math.max(0, Math.min(r0.x + dx, W - r0.w)), y: Math.max(0, Math.min(r0.y + dy, H - r0.h)) });
      return;
    }
    const e0 = drag.current.mode; // handle config with l/r/t/b flags
    let { x, y, w, h } = r0;
    if (e0.l) { const nx = Math.max(0, Math.min(r0.x + dx, r0.x + r0.w - MIN)); w = r0.w + (r0.x - nx); x = nx; }
    if (e0.r) { w = Math.max(MIN, Math.min(r0.w + dx, W - r0.x)); }
    if (e0.t) { const ny = Math.max(0, Math.min(r0.y + dy, r0.y + r0.h - MIN)); h = r0.h + (r0.y - ny); y = ny; }
    if (e0.b) { h = Math.max(MIN, Math.min(r0.h + dy, H - r0.y)); }
    setRect({ x, y, w, h });
  }, []);
  const onUp = useCallback(() => { drag.current = null; }, []);
  useEffect(() => {
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false }); window.addEventListener('touchend', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp); };
  }, [onMove, onUp]);

  const apply = () => {
    const el = imgRef.current; if (!el) return;
    const sx = el.naturalWidth / el.clientWidth, sy = el.naturalHeight / el.clientHeight;
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(rect.w * sx)); c.height = Math.max(1, Math.round(rect.h * sy));
    const ctx = c.getContext('2d');
    const im = new Image();
    im.onload = () => { ctx.drawImage(im, rect.x * sx, rect.y * sy, rect.w * sx, rect.h * sy, 0, 0, c.width, c.height); onCrop(c.toDataURL('image/png')); };
    im.src = image;
  };

  // handle position style helpers
  const cornerPos = { nw: { left: -7, top: -7 }, ne: { right: -7, top: -7 }, se: { right: -7, bottom: -7 }, sw: { left: -7, bottom: -7 } };
  const edgePos = {
    n: { left: '50%', top: -5, ml: '-11px', w: 22, h: 8 }, s: { left: '50%', bottom: -5, ml: '-11px', w: 22, h: 8 },
    e: { top: '50%', right: -5, mt: '-11px', w: 8, h: 22 }, w: { top: '50%', left: -5, mt: '-11px', w: 8, h: 22 },
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eef2f5' }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: '#e1f1f2', color: ACCENT_DARK, display: 'grid', placeItems: 'center' }}><CropIcon sx={{ fontSize: 18 }} /></Box>
          <Box><Typography sx={{ fontWeight: 700, fontSize: 16, color: '#14232b', lineHeight: 1.1 }}>Crop the signature</Typography>
            <Typography sx={{ fontSize: 12, color: '#7b96a2' }}>Frame just your signature — drag any edge or corner.</Typography></Box>
        </Stack>
        <IconButton size="small" onClick={onCancel}><CloseIcon /></IconButton>
      </Box>
      <DialogContent sx={{ p: 0, bgcolor: '#0d1417' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: { xs: 2, md: 3 }, minHeight: 300, backgroundImage: 'radial-gradient(circle at 50% 40%, #1b2830, #0d1417)' }}>
          <Box sx={{ position: 'relative', display: 'inline-block', lineHeight: 0, borderRadius: 1, overflow: 'hidden' }}>
            <img ref={imgRef} src={image} onLoad={onImgLoad} alt="to crop" draggable={false}
              style={{ maxWidth: '100%', maxHeight: 440, display: 'block', userSelect: 'none' }} />
            {ready && (
              <Box onMouseDown={(e) => down('move', e)} onTouchStart={(e) => down('move', e)}
                sx={{ position: 'absolute', left: rect.x, top: rect.y, width: rect.w, height: rect.h, cursor: 'grab',
                  boxShadow: '0 0 0 9999px rgba(10,15,20,0.62)', outline: `2px solid ${ACCENT}`, boxSizing: 'border-box' }}>
                {/* rule-of-thirds grid */}
                <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none',
                  backgroundImage: 'linear-gradient(rgba(255,255,255,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.35) 1px, transparent 1px)',
                  backgroundSize: '33.33% 33.33%', backgroundPosition: '0 0', opacity: 0.5 }} />
                {/* corner handles */}
                {['nw', 'ne', 'se', 'sw'].map((k) => {
                  const cfg = HANDLES.find((hh) => hh.k === k);
                  return (
                    <Box key={k} onMouseDown={(e) => down(cfg, e)} onTouchStart={(e) => down(cfg, e)}
                      sx={{ position: 'absolute', ...cornerPos[k], width: 14, height: 14, borderRadius: '3px', bgcolor: '#fff', border: `2px solid ${ACCENT}`, cursor: cfg.cur, boxShadow: '0 1px 3px rgba(0,0,0,.4)' }} />
                  );
                })}
                {/* edge handles */}
                {['n', 'e', 's', 'w'].map((k) => {
                  const cfg = HANDLES.find((hh) => hh.k === k); const ep = edgePos[k];
                  return (
                    <Box key={k} onMouseDown={(e) => down(cfg, e)} onTouchStart={(e) => down(cfg, e)}
                      sx={{ position: 'absolute', left: ep.left, top: ep.top, right: ep.right, bottom: ep.bottom, ml: ep.ml, mt: ep.mt, width: ep.w, height: ep.h, borderRadius: '4px', bgcolor: '#fff', border: `2px solid ${ACCENT}`, cursor: cfg.cur, boxShadow: '0 1px 3px rgba(0,0,0,.4)' }} />
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ px: 3, py: 2, bgcolor: '#fff' }}>
          <Button startIcon={<RestartAltIcon />} onClick={reset} sx={{ textTransform: 'none', color: '#64757d' }}>Reset</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onCancel} sx={{ textTransform: 'none', color: '#64757d' }}>Cancel</Button>
          <Button variant="contained" startIcon={<CropIcon />} onClick={apply} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK }, px: 2.5 }}>Apply crop</Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
